/* eslint-disable import/exports-last */
import useDebounce from '@/helpers/hooks/useDebounce'
import { useCallback, useEffect, useRef, useState } from 'react'

/* eslint-disable @typescript-eslint/no-explicit-any */
export type Subscription = { unsubscribe: () => void }
export const DEBOUNCE_MS = 33
function useTriggerOnFormValueChanges({
  state,
  watch,
  trigger,
  validateAll,
}: {
  state: { isReady: boolean }
  validateAll?: boolean
  trigger: () => Promise<unknown>
  watch: (fn: (...args: any[]) => void) => Subscription
}): void {
  const [ready, setReady] = useState(state.isReady)
  const inProgressRef = useRef(false)
  const calledWhileInProgressRef = useRef(false)

  useEffect(() => {
    const { isReady } = state
    setReady(isReady)
  }, [state])

  const safeTriggerAsync = useCallback(async () => {
    if (inProgressRef.current) {
      calledWhileInProgressRef.current = true
      return
    }
    inProgressRef.current = true
    try {
      await trigger()
    } catch (e) {
      console.error(e)
    }
    inProgressRef.current = false
    if (!calledWhileInProgressRef.current) return
    await safeTriggerAsync()
    calledWhileInProgressRef.current = false
  }, [trigger])

  const safeTrigger = useCallback(() => void safeTriggerAsync().then(), [safeTriggerAsync])
  const safeTriggerDebounced = useDebounce(safeTrigger, DEBOUNCE_MS)

  useEffect(() => {
    if (!(ready && validateAll)) return
    const sub = watch(() => safeTriggerDebounced())
    return () => sub.unsubscribe()
  }, [ready, safeTriggerDebounced, trigger, validateAll, watch])
}

export default useTriggerOnFormValueChanges
